import {
  Box,
  FormControl,
  FormLabel,
  Select,
  HStack,
  Icon,
  IconButton,
  List,
  ListItem,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { MdCheckCircle, MdClose, MdInfo, MdUploadFile } from 'react-icons/md';
import { toast } from 'react-toastify';

import { FormDatePicker } from '@/components/form/FormDatePicker';
import { ModalForm } from '@/components/ui/Modal/ModalForm';

import { useUploadSettlementImportMutation } from '../api';
import { SettlementImportFormat } from '../types';

interface SettlementImportUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  file: File | null;
  format: SettlementImportFormat;
  importDate: Date | null;
}

export const SettlementImportUploadModal: React.FC<SettlementImportUploadModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { handleSubmit, reset, setValue, watch, control, register } = useForm<FormData>({
    defaultValues: {
      file: null,
      format: 'warsaw',
      importDate: new Date(),
    },
  });

  const selectedFile = watch('file');
  const [uploadImport, { isLoading }] = useUploadSettlementImportMutation();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setValue('file', acceptedFiles[0]);
      }
    },
    [setValue]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt', '.sta', '.mt940'],
    },
    maxFiles: 1,
  });

  const handleRemoveFile = () => {
    setValue('file', null);
  };

  const formatDateYYYYMMDD = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const onSubmit = async (data: FormData) => {
    if (!data.file) {
      toast.error('Proszę wybrać plik');
      return;
    }

    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('format', data.format);
    if (data.importDate) {
      formData.append('importDate', formatDateYYYYMMDD(data.importDate));
    }

    try {
      const result = await uploadImport(formData).unwrap();
      if (result.unrecognizedCount) {
        toast.warning(`Zapisano import. Nierozpoznane wpłaty: ${result.unrecognizedCount}.`);
      } else {
        toast.success('Plik został pomyślnie zaimportowany');
      }
      reset();
      onClose();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Wystąpił błąd podczas importu pliku');
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      title="Import rozliczeń bankowych"
      formId="settlement-import-form"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isLoading}
      saveButtonLabel="Importuj plik"
    >
      <VStack spacing={6} align="stretch" width="100%">
        <FormControl>
          <FormLabel htmlFor="statement-format">Format wyciągu</FormLabel>
          <Select id="statement-format" {...register('format')}>
            <option value="warsaw">Warszawa</option>
            <option value="sgb">SGB</option>
          </Select>
        </FormControl>
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={3}>
            Wybierz plik z wyciągiem bankowym
          </Text>

          {!selectedFile ? (
            <Box
              {...getRootProps()}
              cursor="pointer"
              borderWidth={2}
              borderStyle="dashed"
              borderColor={isDragActive ? 'purple.400' : 'gray.300'}
              borderRadius="lg"
              bg={isDragActive ? 'purple.50' : 'gray.50'}
              p={8}
              textAlign="center"
              position="relative"
              transition="all 0.2s"
              _hover={{
                borderColor: 'purple.400',
                bg: 'purple.50',
              }}
            >
              <input {...getInputProps()} />
              <VStack spacing={2} position="relative" zIndex={1}>
                <Text fontSize="md" fontWeight="medium" color="gray.700">
                  {isDragActive ? 'Upuść plik tutaj' : 'Przeciągnij plik lub kliknij aby wybrać'}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Obsługiwane formaty: MT940, STA, TXT
                </Text>
              </VStack>
            </Box>
          ) : (
            <Box borderWidth={1} borderColor="purple.200" borderRadius="lg" bg="purple.50" p={4}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Icon as={MdUploadFile} boxSize={6} color="purple.600" />
                  <Box>
                    <Text fontWeight="medium" color="gray.700">
                      {selectedFile.name}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </Text>
                  </Box>
                </HStack>
                <IconButton
                  aria-label="Usuń plik"
                  icon={<MdClose />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={handleRemoveFile}
                />
              </HStack>
            </Box>
          )}
        </Box>

        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={3}>
            Data importu
          </Text>
          <FormDatePicker
            name="importDate"
            control={control}
            placeholderText="Wybierz datę importu"
            dateFormat="dd/MM/yyyy"
          />
        </Box>

        <Box bg="gray.50" p={4} borderRadius="md">
          <HStack spacing={2} mb={2}>
            <Icon as={MdInfo} color="gray.600" />
            <Text fontSize="sm" fontWeight="medium">
              Informacje o pliku
            </Text>
          </HStack>
          <List spacing={1}>
            <ListItem fontSize="sm" color="gray.600">
              <HStack spacing={2}>
                <Icon as={MdCheckCircle} color="green.500" boxSize={4} />
                <Text>Obsługiwane formaty: MT940, STA, TXT</Text>
              </HStack>
            </ListItem>
            <ListItem fontSize="sm" color="gray.600">
              <HStack spacing={2}>
                <Icon as={MdCheckCircle} color="green.500" boxSize={4} />
                <Text>Maksymalny rozmiar: 10 MB</Text>
              </HStack>
            </ListItem>
            <ListItem fontSize="sm" color="gray.600">
              <HStack spacing={2}>
                <Icon as={MdCheckCircle} color="green.500" boxSize={4} />
                <Text>SGB: UTF-8, Windows-1250</Text>
              </HStack>
            </ListItem>
          </List>
        </Box>
      </VStack>
    </ModalForm>
  );
};
